/* ==========================================================================
   EB Video coverflow
   A graduated-depth coverflow: the centre card is tallest, each card outward
   steps a little shorter, cards overlap with the active one on top. One float
   `pos` drives every card's height + fan position; dragging resizes cards
   continuously (not a hard snap). The active card's video autoplays in view and
   follows the centre as you swipe. Absolute-positioned card model (each card
   placed from its signed distance to `pos`).
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  class EbVideoCoverflow extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.eb-vc__viewport');
      this.stage = this.querySelector('[data-eb-vc-stage]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('.eb-vc__card'));
      if (!this.viewport || !this.stage || !this.cards.length) return;

      this.N = this.cards.length;
      this.REAL = parseInt(this.stage.getAttribute('data-real'), 10) || this.N;
      this.PREPEND = parseInt(this.stage.getAttribute('data-prepend'), 10) || 0;
      this.START = parseInt(this.stage.getAttribute('data-start'), 10) || 0;
      this.pos = this.PREPEND + this.START;
      this.lastActive = -1;
      this.raf = null;
      this.didDrag = false;

      // Playback config from the section.
      this.autoplay = this.getAttribute('data-autoplay') === 'true' && !reduceMotion;
      this.loopVideos = this.getAttribute('data-loop') === 'true';
      this.soundOn = this.getAttribute('data-muted') !== 'true';
      this.classList.toggle('is-sound-on', this.soundOn);
      this.inView = false;
      this.playing = null;

      // Auto-rotate: advance the fan on a timer. Honour reduced-motion (auto-advancing
      // motion should stay off for those users). Paused on hover / drag / off-screen.
      this.autoRotate = this.getAttribute('data-auto-rotate') === 'true' && !reduceMotion;
      this.rotateMode = this.getAttribute('data-rotate-mode') || 'video'; // 'video' | 'timer'
      this.rotateMs = (parseInt(this.getAttribute('data-rotate-speed'), 10) || 5) * 1000;
      this._hover = false;
      this._dragging = false;

      this.prevBtn = this.querySelector('[data-eb-vc-prev]');
      this.nextBtn = this.querySelector('[data-eb-vc-next]');

      this.readMetrics();
      this.bindControls();
      this.bindDrag();
      this.bindVideoState();
      this.layout(this.pos);
      this.positionArrows();

      // Autoplay gate: only the active video, only while the section is on screen.
      this.io = new IntersectionObserver(function (entries) {
        this.inView = entries[0].isIntersecting;
        if (this.inView) { this.playActive(); this.startAutoRotate(); }
        else { if (this.playing) this.playing.pause(); this.stopAutoRotate(); }
      }.bind(this), { threshold: 0.35 });
      this.io.observe(this);

      this._onResize = this.onResize.bind(this);
      window.addEventListener('resize', this._onResize);
      this._onVis = function () {
        if (document.hidden) { if (this.playing) this.playing.pause(); this.stopAutoRotate(); }
        else { this.playActive(); this.startAutoRotate(); }
      }.bind(this);
      document.addEventListener('visibilitychange', this._onVis);

      // Self-correct after paint: the first read above can be too early (esp. a
      // theme-editor live re-render, or late web fonts / images). Re-measure next
      // frame, and keep re-measuring whenever the viewport gets its real size.
      this._raf2 = requestAnimationFrame(this.remeasure.bind(this));
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.remeasure.bind(this));
        this.ro.observe(this.viewport);
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this._raf2) cancelAnimationFrame(this._raf2);
      this.stopAutoRotate();
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('visibilitychange', this._onVis);
    }

    /* ---- Sizes: read back from the CSS (single source of truth). Width/height
       come from RENDERED boxes so vw/dvh resolve; the rest are unitless props.
       In the theme editor a live re-render reconnects this element before the new
       DOM has painted, so the boxes can read 0 — never let a transient 0 clobber a
       good measurement, and start from sane current defaults (not stale numbers). ---- */
    readMetrics() {
      var cs = getComputedStyle(this);
      var activeCard = this.cards[Math.round(this.pos)] || this.cards[0];
      var h = this.stage.getBoundingClientRect().height;
      var w = activeCard.getBoundingClientRect().width;
      if (h > 0) this.H = h;
      if (w > 0) this.W = w;
      if (!this.H) this.H = 427;   // fallback only until a real measurement lands
      if (!this.W) this.W = 240;
      this.STEP = this.W * (parseFloat(cs.getPropertyValue('--eb-vc-step-ratio')) || 0.8);
      this.DECAY = parseFloat(cs.getPropertyValue('--eb-vc-decay')) || 0.85;
      this.MIN = parseFloat(cs.getPropertyValue('--eb-vc-min')) || 0.6;
      this.VISIBLE = parseFloat(cs.getPropertyValue('--eb-vc-visible')) || 1.6;
    }

    /* Re-read sizes and re-lay-out. Called after paint / on any real resize so the
       fan self-corrects even if the first synchronous read happened too early. */
    remeasure() {
      this.readMetrics();
      this.layout(this.pos);
      this.positionArrows();
    }

    /* ---- Layout: size every card by its distance to `pos`, fan it out, stack it. ---- */
    layout(p) {
      // Fade band = the fractional part of VISIBLE, so the outermost card sits at
      // full opacity when settled and eases to 0 exactly as it reaches the edge.
      var band = this.VISIBLE - Math.floor(this.VISIBLE);
      if (band < 0.15) band = 0.5; // integer VISIBLE → sensible fade width
      for (var i = 0; i < this.N; i++) {
        var card = this.cards[i];
        var d = i - p;
        var ad = Math.abs(d);
        if (ad > this.VISIBLE) { card.style.display = 'none'; continue; }
        card.style.display = '';
        var f = Math.max(this.MIN, Math.pow(this.DECAY, ad)); // graduated height factor
        card.style.height = (this.H * f) + 'px';
        card.style.transform = 'translate(-50%, -50%) translateX(' + (d * this.STEP) + 'px)';
        card.style.zIndex = String(Math.round(1000 - ad * 10));
        card.style.setProperty('--eb-vc-dim', (Math.min(ad, 1) * 0.18).toFixed(3));
        // Crossfade the edge card in/out instead of popping it with display.
        card.style.opacity = clamp((this.VISIBLE - ad) / band, 0, 1).toFixed(3);
        card.classList.toggle('is-active', Math.round(p) === i);
      }
      this.onActiveMaybeChanged(Math.round(p));
    }

    /* ---- Programmatic move (arrows, keyboard, tap-to-centre, release snap). ---- */
    cancelRaf() { if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; } }
    settle() { this.normalize(); this.positionArrows(); }

    animateTo(target) {
      this.cancelRaf();
      if (reduceMotion) { this.pos = target; this.layout(this.pos); this.settle(); return; }
      var self = this, start = this.pos, t0 = null, dur = 460;
      var frame = function (t) {
        if (t0 === null) t0 = t;
        var e = clamp((t - t0) / dur, 0, 1);
        self.pos = start + (target - start) * easeInOut(e);
        self.layout(self.pos);
        if (e < 1) self.raf = requestAnimationFrame(frame);
        else { self.raf = null; self.pos = target; self.layout(self.pos); self.settle(); }
      };
      this.raf = requestAnimationFrame(frame);
    }

    /* Keep the settled index inside the real window; the clone jump is invisible. */
    normalize() {
      var changed = false;
      while (Math.round(this.pos) < this.PREPEND) { this.pos += this.REAL; changed = true; }
      while (Math.round(this.pos) >= this.PREPEND + this.REAL) { this.pos -= this.REAL; changed = true; }
      if (changed) this.layout(this.pos);
    }

    positionArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      var mid = this.viewport.getBoundingClientRect().width / 2;
      var half = this.W / 2 + 12;
      this.prevBtn.style.left = Math.max(0, mid - half - 44) + 'px';
      this.prevBtn.style.right = 'auto';
      this.nextBtn.style.left = (mid + half) + 'px';
      this.nextBtn.style.right = 'auto';
    }

    /* ---- Auto-rotate: advance to the next card when the ACTIVE card's video finishes
       one full play. Cards with no playable video (placeholder, or video autoplay off)
       fall back to a timer so rotation never stalls. Paused while hovered / dragging /
       off-screen; each new active card re-arms this via onActiveMaybeChanged. ---- */
    startAutoRotate() {
      this.stopAutoRotate();
      if (!this.autoRotate || !this.inView || this._hover || this._dragging) return;
      if (this.rotateMode === 'timer') { this.armFallback(); return; }  // fixed-time mode
      var v = this.videoAt(Math.round(this.pos));
      if (v && this.autoplay) this._awaitVideo = true;  // the video's 'ended' drives the advance
      else this.armFallback();                          // no video to wait on → timer
    }
    // Timer path: advance after rotateMs when there's no video play to wait on.
    armFallback() {
      if (!this.autoRotate || !this.inView || this._hover || this._dragging) return;
      this._awaitVideo = false;
      var self = this;
      this._rotTimer = setTimeout(function () { self.advanceAuto(); }, this.rotateMs);
    }
    stopAutoRotate() {
      this._awaitVideo = false;
      if (this._rotTimer) { clearTimeout(this._rotTimer); this._rotTimer = null; }
    }
    advanceAuto() {
      if (!this.autoRotate || !this.inView || this._hover || this._dragging) return;
      this.animateTo(Math.round(this.pos) + 1);   // onActiveMaybeChanged re-arms for the new card
    }

    /* ---- Controls: arrows, keyboard, tap-to-centre, play toggle, mute ---- */
    bindControls() {
      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.animateTo(Math.round(self.pos) - 1); self.startAutoRotate(); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.animateTo(Math.round(self.pos) + 1); self.startAutoRotate(); });

      this.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.animateTo(Math.round(self.pos) - 1); self.startAutoRotate(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); self.animateTo(Math.round(self.pos) + 1); self.startAutoRotate(); }
      });

      // A real drag ends in a click; swallow it so it doesn't navigate or re-centre.
      this.stage.addEventListener('click', function (e) {
        if (self.didDrag) { e.preventDefault(); e.stopPropagation(); self.didDrag = false; }
      }, true);

      // Tap a card: non-active → centre it; active → toggle play. Controls opt out.
      this.cards.forEach(function (card, i) {
        card.addEventListener('click', function (e) {
          if (self.didDrag) return;
          if (e.target.closest('.eb-vc__product')) return; // let the link navigate
          if (e.target.closest('.eb-vc__mute')) return;     // mute handles itself
          if (i !== Math.round(self.pos)) { e.preventDefault(); self.animateTo(i); self.startAutoRotate(); return; }
          self.togglePlay();
        });
      });

      // Mute / unmute (section-wide sound state).
      this.querySelectorAll('[data-eb-vc-mute]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.stopPropagation(); self.toggleSound(); });
      });

      // Pause auto-rotate while a mouse hovers the section (desktop only — no hover on touch).
      if (window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', function () { self._hover = true; self.stopAutoRotate(); });
        this.addEventListener('mouseleave', function () { self._hover = false; self.startAutoRotate(); });
      }
    }

    /* ---- Drag / swipe (mouse, touch, pen) ---- */
    bindDrag() {
      var self = this;
      var drag = { active: false, decided: false, startX: 0, startY: 0, base: 0, moved: 0, id: null };

      this.viewport.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        self.cancelRaf();
        self.readMetrics();
        drag.active = true; drag.decided = false;
        drag.startX = e.clientX; drag.startY = e.clientY;
        drag.base = self.pos; drag.moved = 0; drag.id = e.pointerId;
        self.didDrag = false;
        self._dragging = true; self.stopAutoRotate();
      });

      this.viewport.addEventListener('pointermove', function (e) {
        if (!drag.active) return;
        var dx = e.clientX - drag.startX, dy = e.clientY - drag.startY;
        if (!drag.decided) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          if (Math.abs(dy) > Math.abs(dx)) { drag.active = false; return; } // vertical → page scroll
          drag.decided = true;
          self.viewport.classList.add('is-grabbing');
          try { self.viewport.setPointerCapture(drag.id); } catch (err) {}
        }
        drag.moved = dx;
        if (e.cancelable) e.preventDefault();
        self.pos = drag.base - dx / self.STEP;   // 1:1 finger tracking + continuous resize
        self.layout(self.pos);
      }, { passive: false });

      var end = function () {
        if (!drag.active) return;
        drag.active = false;
        self._dragging = false;
        self.viewport.classList.remove('is-grabbing');
        if (!drag.decided) { self.startAutoRotate(); return; } // a tap: let the click through
        self.didDrag = Math.abs(drag.moved) > 6;
        self.animateTo(Math.round(self.pos));
        self.startAutoRotate();
      };
      this.viewport.addEventListener('pointerup', end);
      this.viewport.addEventListener('pointercancel', end);
    }

    /* ---- Video playback ---- */
    videoAt(i) { var c = this.cards[i]; return c ? c.querySelector('video') : null; }

    // Promote data-src → src the first time a video is needed, then load().
    ensureLoaded(v) {
      if (!v || v.dataset.ready) return;
      var srcs = v.querySelectorAll('source[data-src]');
      for (var i = 0; i < srcs.length; i++) srcs[i].src = srcs[i].getAttribute('data-src');
      v.load();
      v.dataset.ready = '1';
    }

    playActive() {
      var v = this.videoAt(Math.round(this.pos));
      if (!v) return;
      this.ensureLoaded(v);
      // In video mode auto-rotate advances on 'ended', so the active card can't loop.
      v.loop = (this.autoRotate && this.rotateMode === 'video') ? false : this.loopVideos;
      v.muted = !this.soundOn;                    // muted is required to autoplay
      if (v.ended) v.currentTime = 0;             // replay cleanly if we rotate back to it
      if (this.autoplay && this.inView) {
        var self = this;
        var pr = v.play();
        if (pr && pr.catch) pr.catch(function () {
          // Autoplay blocked → this video won't 'end'; fall back to the timer.
          if (self.autoRotate && self._awaitVideo) self.armFallback();
        });
      }
      if (this.playing && this.playing !== v) this.playing.pause();
      this.playing = v;
    }

    // Switch the video only when the centre card actually changes (called from layout).
    onActiveMaybeChanged(active) {
      if (active === this.lastActive) return;
      this.lastActive = active;
      for (var i = 0; i < this.N; i++) {
        if (i !== active) { var v = this.videoAt(i); if (v) v.pause(); }
      }
      this.playActive();
      this.startAutoRotate();   // re-arm the auto-advance trigger for the new active card
    }

    togglePlay() {
      var v = this.playing || this.videoAt(Math.round(this.pos));
      if (!v) return;
      if (v.paused) { this.ensureLoaded(v); var pr = v.play(); if (pr && pr.catch) pr.catch(function () {}); }
      else v.pause();
    }

    toggleSound() {
      this.soundOn = !this.soundOn;
      this.classList.toggle('is-sound-on', this.soundOn);
      if (this.playing) {
        this.playing.muted = !this.soundOn;
        if (this.soundOn) { var pr = this.playing.play(); if (pr && pr.catch) pr.catch(function () {}); }
      }
    }

    // Reflect real play/pause state on each card so the play overlay hides while playing.
    bindVideoState() {
      var self = this;
      this.cards.forEach(function (card, i) {
        var v = card.querySelector('video');
        if (!v) return;
        v.addEventListener('play', function () { card.classList.add('is-playing'); });
        v.addEventListener('pause', function () { card.classList.remove('is-playing'); });
        v.addEventListener('ended', function () {
          card.classList.remove('is-playing');
          // Auto-rotate: the active card's video finished one play → advance.
          if (self._awaitVideo && i === Math.round(self.pos)) self.advanceAuto();
        });
      });
    }

    onResize() {
      clearTimeout(this._rz);
      this._rz = setTimeout(function () {
        this.readMetrics();
        this.layout(this.pos);
        this.positionArrows();
      }.bind(this), 120);
    }
  }

  if (!customElements.get('eb-video-coverflow')) {
    customElements.define('eb-video-coverflow', EbVideoCoverflow);
  }
})();